// Order Types
export interface Order {
  order_id: string;
  order_status: string; // PENDING, CONFIRMED, CANCELLED, etc.
  total_amount: string; // API returns as string
  discount_amount: string;
  final_amount: string;
  items_count: number;
  modification_count: number;
  eta?: string | null; // Estimated time of arrival
  delivery_date?: string | null;
  created_at: string;
  updated_at: string;
  // Additional fields that might be in detail API
  items?: OrderItem[];
  user_modifications?: UserModifications;
  notes?: string;
  delivery_address?: string;
  contact_phone?: string;
  outlet_id?: string;
}

export interface OrderItem {
  sku: string;
  name: string;
  price: string;
  category: string;
  quantity: number;
  packaging: {
    moq: number;
    unit: string;
    pack_size: string;
    description: string;
  };
  unit_price: number;
  total_price: number;
}

export type OrderStatus = 
  | 'PENDING'           // Đang chờ
  | 'PROPOSED'          // Đề xuất
  | 'CONFIRMED'         // Đã xác nhận
  | 'SHIPPING'          // Đang giao hàng
  | 'DELIVERED'         // Đã giao hàng
  | 'COMPLETED'         // Hoàn thành
  | 'CANCELLED'         // Hủy
  | 'RETURN';           // Trả hàng

export interface OrderStats {
  total_orders: number;
  pending_orders: number;
  confirmed_orders: number;
  processing_orders: number;
  completed_orders: number;
  cancelled_orders: number;
  total_amount: number;
  average_order_value: number;
  orders_trend: {
    value: number;
    is_positive: boolean;
  };
}

export interface OrdersResponse {
  success: boolean;
  data: {
    orders: Order[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
      has_more: boolean;
      current_page: number;
      total_pages: number;
    };
    filters: {
      applied_filters: Record<string, any>;
      outlet_info: {
        outlet_id: string;
        outlet_name: string;
        region: string;
        city_tier: string;
        outlet_type: string;
      };
    };
    summary: {
      total_orders: number;
      total_amount: string;
      status_breakdown: {
        PENDING: number;
        PROPOSED: number;
        CONFIRMED: number;
        SHIPPING: number;
        DELIVERED: number;
        CANCELLED: number;
        RETURN: number;
      };
    };
  };
  message?: string;
}

export interface OrderDetailsResponse {
  success: boolean;
  data: {
    order: Order;
  };
  message?: string;
}

export interface UserModifications {
  modifications: any[];
  has_modifications: boolean;
  modification_count: number;
  original_suggestion: {
    total_amount: number;
    suggested_items: OrderItem[];
    confidence_score: number;
  };
}

// Helper function to get Vietnamese status text
export const getOrderStatusText = (status: string): string => {
  // Map API status to Vietnamese - chỉ dùng status chuẩn từ API
  const statusMap: Record<string, string> = {
    PENDING: 'Đang chờ',
    PROPOSED: 'Đề xuất', 
    CONFIRMED: 'Đã xác nhận',
    SHIPPING: 'Đang giao hàng',
    DELIVERED: 'Đã giao hàng',
    COMPLETED: 'Hoàn thành',
    CANCELLED: 'Hủy',
    RETURN: 'Trả hàng'
  };
  return statusMap[status] || status;
};

// Helper function to get status color class
export const getOrderStatusColor = (status: string): string => {
  // Map API status to colors - chỉ dùng status chuẩn từ API
  const colorMap: Record<string, string> = {
    PENDING: 'bg-yellow-600 text-white hover:bg-yellow-500 ',
    PROPOSED: 'bg-blue-600 text-white hover:bg-blue-500',
    CONFIRMED: 'bg-green-600 text-white hover:bg-green-500',
    SHIPPING: 'bg-purple-600 text-white hover:bg-purple-500',
    DELIVERED: 'bg-emerald-600 text-white hover:bg-emerald-500',
    COMPLETED: 'bg-gray-600 text-white hover:bg-gray-500',
    CANCELLED: 'bg-red-600 text-white hover:bg-red-500',
    RETURN: 'bg-orange-600 text-white hover:bg-orange-500'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

// Helper function to format currency
export const formatCurrency = (amount: string | number, currency: string = 'VND'): string => {
  if (!amount && amount !== 0) return '0 ₫';
  
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  if (isNaN(numAmount)) {
    return '0 ₫';
  }
  
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: currency === 'VND' ? 'VND' : 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numAmount);
};

// Helper function to format date
export const formatOrderDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'N/A';
  }
  
  return date.toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Helper function to check if order is pending or processing
export const isOrderPendingOrProcessing = (status: string): boolean => {
  // Chỉ check status chuẩn từ API
  return status === 'PENDING' || status === 'PROPOSED';
};
